import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * 对话滚动由用户阅读意图控制。观察内容高度可覆盖流式增长、图片/公式加载和工具展开，
 * 每帧最多滚动一次；切会话重新定位，卸载时释放观察器和事件。
 * @param sessionId 当前稳定会话 ID，变化时不继承上一会话的阅读位置。
 */
export function useConversationScroll(sessionId: string | undefined) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const followingRef = useRef(true);
  const [following, setFollowingState] = useState(true);
  /** 同步引用先于 React 重绘，避免用户上翻与同帧流式增长发生竞争。 */
  const setFollowing = useCallback((value: boolean) => {
    followingRef.current = value;
    setFollowingState(value);
    if (value && containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }, []);
  /** 只有用户回到底部才重新跟随；滚动内容增长不会强制覆盖主动暂停。 */
  const onScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 80;
    followingRef.current = nearBottom;
    setFollowingState(nearBottom);
  }, []);
  useLayoutEffect(() => {
    const container = containerRef.current, content = contentRef.current;
    if (!container || !content) return;
    setFollowing(true);
    let frame = 0;
    /** 批量处理布局变化，不使用连续 smooth 滚动导致反复动画或抢焦点。 */
    const resize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (followingRef.current) container.scrollTop = container.scrollHeight;
      });
    };
    const wheel = (event: WheelEvent) => { if (event.deltaY < 0) setFollowing(false); };
    const touch = () => setFollowing(false);
    const keyboard = (event: KeyboardEvent) => {
      if (['ArrowUp', 'PageUp', 'Home'].includes(event.key)) setFollowing(false);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(content); observer.observe(container);
    container.addEventListener('wheel', wheel, { passive: true });
    container.addEventListener('touchstart', touch, { passive: true });
    container.addEventListener('keydown', keyboard);
    return () => {
      cancelAnimationFrame(frame); observer.disconnect();
      container.removeEventListener('wheel', wheel); container.removeEventListener('touchstart', touch);
      container.removeEventListener('keydown', keyboard);
    };
  }, [sessionId, setFollowing]);
  return { containerRef, contentRef, following, setFollowing, onScroll };
}
